import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams } from 'react-router-dom';
import { Search, X, Package2, CheckCircle2, AlertCircle } from "lucide-react";
import PartCard from "./PartCard";
import { useMobile } from "../../utils/useMobile";
import { motion, AnimatePresence } from "framer-motion";

// Memoized PartCard component to prevent unnecessary re-renders
const MemoizedPartCard = React.memo(PartCard);

// Toast component (basic example)
const Toast = ({ message, type, onDismiss }) => {
  useEffect(() => {
    if (message) { // Only set timer if there's a message
      const timer = setTimeout(onDismiss, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onDismiss]); // Add message to dependency array

  if (!message) return null;

  const bgColor = type === 'success' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-red-500 to-pink-500';
  const Icon = type === 'success' ? CheckCircle2 : AlertCircle;

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={`fixed bottom-20 left-1/2 -translate-x-1/2 p-4 rounded-lg text-white ${bgColor} shadow-lg z-50 flex items-center gap-2`}
    >
      <Icon className="h-5 w-5" />
      <span>{message}</span>
    </motion.div>
  );
};

export default function MobileSearchView() {
  const { sessionId } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [toast, setToast] = useState({ message: null, type: null });
  const searchInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const isMobile = useMobile();
  
  // Memoize the API base URL to prevent recalculation on each render
  const apiBaseUrl = useMemo(() => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocalhost 
      ? `${window.location.protocol}//${window.location.host}`
      : window.location.origin;
  }, []);

  // Log sessionId for debugging
  useEffect(() => {
    if (sessionId) {
      console.log('(CLIENT MobileSearchView) Session ID:', sessionId);
    }
  }, [sessionId]);

  // Memoize the fetch function to prevent recreation on each render
  const fetchSearchResults = useCallback(async (term) => {
    if (!term.trim()) return [];
    
    console.log(`(CLIENT MobileSearchView) Fetching results for: "${term}"`);
    setIsLoading(true);
    setError(null);
    
    try {
      const apiUrl = `${apiBaseUrl}/api/search?q=${encodeURIComponent(term)}`;
      console.log(`(CLIENT MobileSearchView) API URL: ${apiUrl}`);
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`(CLIENT MobileSearchView) API error: ${response.status} - ${errorText}`);
        throw new Error(`Error: ${response.status} - ${errorText}`);
      }
      
      const data = await response.json();
      console.log(`(CLIENT MobileSearchView) Received ${data.length} results for "${term}":`, data);
      return data;
    } catch (err) {
      console.error("(CLIENT MobileSearchView) Error fetching search results:", err);
      setError(`Failed to fetch results: ${err.message}`);
      setToast({ message: `Search error: ${err.message}`, type: 'error' });
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setSearchResults([]);
      return;
    }

    const debounceTimer = setTimeout(async () => {
      console.log(`(CLIENT MobileSearchView) Searching for: "${searchTerm}"`);
      const results = await fetchSearchResults(searchTerm);
      console.log(`(CLIENT MobileSearchView) Got ${results.length} results for: "${searchTerm}"`);
      setSearchResults(results);
    }, 300); // Reduced from 500ms to 300ms for better responsiveness

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, fetchSearchResults]);

  const handleShowToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const handleDismissToast = () => {
    setToast({ message: null, type: null });
  };

  // Function to send selection to backend
  const sendPartSelectionToBackend = useCallback(async (partOrNull) => {
    try {
      if (partOrNull) {
        const selectPartApiUrl = `${apiBaseUrl}/api/select-part`;
        await fetch(selectPartApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, ...partOrNull }),
        });
        console.log("(CLIENT MobileSearchView) Sent selection to backend:", partOrNull, "Session:", sessionId);
      } else {
        const clearUrl = `${apiBaseUrl}/api/clear-selection`;
        await fetch(clearUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId }),
        });
        console.log("(CLIENT MobileSearchView) Sent clear selection to backend. Session:", sessionId);
      }
    } catch (err) {
      console.error("(CLIENT MobileSearchView) Error sending selection to backend:", err);
      handleShowToast(`Error updating projector: ${err.message}`, 'error');
    }
  }, [apiBaseUrl, sessionId, handleShowToast]);

  const handleSelectPart = useCallback(async (part) => {
    if (showSuccessAnimation) return; // Prevent selection during success animation

    if (selectedPart && selectedPart['Bin Number'] === part['Bin Number']) {
      setSelectedPart(null); // Deselect if tapping the same card
      await sendPartSelectionToBackend(null); // Clear projector on deselect
    } else {
      setSelectedPart(part);
      await sendPartSelectionToBackend(part); // Send new selection to projector
      // Smooth scroll to the selected card (optional, implemented if card has an id)
      const cardElement = document.getElementById(`part-card-${part['Bin Number']}`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedPart, showSuccessAnimation, sendPartSelectionToBackend]);

  const handleFoundIt = useCallback(async () => {
    if (!selectedPart) return;

    setShowSuccessAnimation(true);
    // handleShowToast('Part found and logged!', 'success'); // REMOVED: User requested no toast on "Found It!"

    try {
      // Clear selection on backend as the part is now "found"
      await sendPartSelectionToBackend(null);
      console.log("(CLIENT MobileSearchView) Cleared selection after 'Found It!' for session:", sessionId);
    } catch (err) {
      console.error("(CLIENT MobileSearchView) Error clearing selection after 'Found It!':", err);
      handleShowToast(`Error clearing projector: ${err.message}`, 'error');
    }

    setTimeout(() => {
      setShowSuccessAnimation(false);
      setSelectedPart(null); // Clear selected part on mobile
      setSearchTerm("");
      setSearchResults([]);
      searchInputRef.current?.focus(); // Focus search input
    }, 1500); // Duration for success animation and toast display
  }, [selectedPart, sendPartSelectionToBackend, sessionId, handleShowToast]);

  const clearSearchAndSelection = useCallback(async () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedPart(null);
    setShowSuccessAnimation(false); // Ensure success animation is also cleared
    await sendPartSelectionToBackend(null); // Clear projector
    searchInputRef.current?.focus(); // Focus search input
  }, [sendPartSelectionToBackend]);

  // Memoize search results to prevent unnecessary re-renders
  const renderedSearchResults = useMemo(() => {
    if (!searchResults.length && !isLoading) return null;
    
    // Skeleton loading state
    if (isLoading) {
      return Array(3).fill(0).map((_, index) => (
        <motion.div
          key={`skeleton-${index}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1 }}
          className="w-full p-5 bg-white rounded-xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.03)] mb-3"
        >
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-3 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
            <div className="h-8 w-16 bg-gray-200 rounded-full"></div>
          </div>
        </motion.div>
      ));
    }

    return searchResults.map((part, index) => (
      <motion.div
        key={part['Bin Number'] || index}
        id={`part-card-${part['Bin Number']}`}
        layout
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ type: "spring", stiffness: 400, damping: 35 }}
      >
        <MemoizedPartCard
          part={part}
          onSelect={handleSelectPart}
          isSelected={selectedPart && selectedPart['Bin Number'] === part['Bin Number']}
          showSuccess={showSuccessAnimation && selectedPart && selectedPart['Bin Number'] === part['Bin Number']}
          onFoundIt={handleFoundIt}
        />
      </motion.div>
    ));
  }, [searchResults, isLoading, handleSelectPart, selectedPart, showSuccessAnimation, handleFoundIt]);

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Toast message={toast.message} type={toast.type} onDismiss={handleDismissToast} />
      <main className="flex-1 container max-w-md mx-auto p-5">
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="py-6 mb-2 text-center"
        >
          <div className="flex items-center justify-center gap-2 mb-1">
            <Package2 className="h-7 w-7 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Part Finder
            </h1>
          </div>
          <p className="text-gray-500 text-sm">Search for parts and find their bin locations</p>
        </motion.header>

        <motion.div
          layout
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 300, damping: 30 }}
          className="relative mb-8"
        >
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-blue-400" />
          </div>
          <input
            type="text"
            placeholder="e.g., M4 bolt, cable"
            value={searchTerm}
            ref={searchInputRef}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-12 py-4 rounded-xl border-0 bg-white shadow-[0_2px_10px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-2 focus:ring-blue-400 text-gray-900 placeholder:text-gray-400"
          />
          {searchTerm && (
            <button onClick={clearSearchAndSelection} className="absolute inset-y-0 right-4 flex items-center p-2 text-gray-400 hover:text-gray-900">
              <X className="h-5 w-5" />
            </button>
          )}
        </motion.div>

        <motion.div
          layout
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          className="space-y-3 mb-24"
        >
          <AnimatePresence mode="wait">
            {renderedSearchResults}
          </AnimatePresence>
          {!isLoading && searchTerm.trim() !== "" && searchResults.length === 0 && !error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center p-8 text-gray-500"
            >
              <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="mb-1 text-lg text-gray-900">No parts found</p>
              <p className="text-sm">Try a different search term or check for typos.</p>
            </motion.div>
          )}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center p-8 text-red-500 bg-red-50 rounded-lg"
            >
              <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
              <p className="mb-1 text-lg font-semibold">Oops! Something went wrong.</p>
              <p className="text-sm">{error}</p>
            </motion.div>
          )}
        </motion.div>
      </main>

      <AnimatePresence>
        {!selectedPart && searchResults.length > 0 && (
          <motion.footer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-gray-100 p-4 shadow-top-lg"
          >
            <div className="container max-w-md mx-auto">
              <button
                onClick={clearSearchAndSelection}
                className="w-full py-3 text-gray-500 border border-gray-100 rounded-xl font-medium bg-white hover:bg-gray-50 active:bg-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.03)] transition-all"
              >
                Clear Results
              </button>
            </div>
          </motion.footer>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {selectedPart && !showSuccessAnimation && (
          <motion.footer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-md border-t border-gray-100 p-4 shadow-top-lg"
          >
            <div className="container max-w-md mx-auto">
              <button
                onClick={clearSearchAndSelection}
                className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 py-3.5 px-4 rounded-xl font-medium flex items-center justify-center gap-2 shadow-sm active:shadow-xs transition-all duration-150 text-base"
              >
                Back to Search
              </button>
            </div>
          </motion.footer>
        )}
      </AnimatePresence>
    </div>
  );
} 